import { ACTION_TYPES } from "@/utils/ai/utils";
import { create } from "zustand";
import _ from "lodash";
import { Message, MessageContext, useSupabaseStore } from "./supabaseStore";
import { useMemo } from "react";

export enum AI_MODEL {
  CLAUDE_3_7_SONNET = "claude",
  GEMINI_2_0_FLASH = "gemini",
  CLAUDE_3_5_SONNET = "claude-3-5-sonnet",
  CLAUDE_4_0_SONNET = "claude-4-0-sonnet",
  GEMINI_2_5_PRO = "gemini-2.5-pro",
  GPT_4_1 = "gpt-4.1",
}

export enum AI_AGENT {
  CLASSIC = "classic",
  MAX_AGENT = "max-agent",
}

export const SEARCH_SUPPORTED_MODELS = new Set([
  AI_MODEL.GEMINI_2_0_FLASH,
  AI_MODEL.GEMINI_2_5_PRO,
]);

export interface AiActionStore {
  actionType: ACTION_TYPES;
  selectedConversationId: string | null;

  setActionType: (actionType: ACTION_TYPES) => void;
  setSelectionConversationId: (selectedConversationId: string | null) => void;

  chatStreamingMessages: Message[] | null;
  setChatStreamingMessages: (messages: Message[] | null) => void;

  setChatMessageContext: (messageId: string, context: MessageContext) => void;

  resetAiActionStore: () => void;

  reasoningModel: AI_MODEL;
  setReasoningModel: (model: AI_MODEL) => void;

  agent: AI_AGENT;
  setAgent: (agent: AI_AGENT) => void;

  codeGenerationModel: AI_MODEL;
  setCodeGenerationModel: (model: AI_MODEL) => void;

  tokenSoftLimit: number;
  setTokenSoftLimit: (limit: number) => void;

  stopAiTrigger: number;
  setStopAiTrigger: () => void;

  canvasPromptInput: string;
  chatPromptInput: string;
  setPromptInput(type: "canvas" | "chat", prompt: string): void;

  streamingTokenCount: number;
  setStreamingTokenCount: (count: number) => void;
  resetStreamingTokenCount: () => void;
}

export const useAiActionStore = create<AiActionStore>(
  (set, get): AiActionStore => {
    return {
      actionType: ACTION_TYPES.GENERATE,
      selectedConversationId: null,
      reasoningModel: AI_MODEL.CLAUDE_4_0_SONNET,
      codeGenerationModel: AI_MODEL.CLAUDE_4_0_SONNET,
      agent: AI_AGENT.CLASSIC,
      setReasoningModel: (model) => {
        set({ reasoningModel: model });
      },

      setCodeGenerationModel: (model) => {
        set({ codeGenerationModel: model });
      },

      setAgent: (agent) => {
        set({ agent });
      },

      setActionType: (actionType: ACTION_TYPES) => {
        set({ actionType });
      },
      setSelectionConversationId: (selectionConversationId) => {
        set({ selectedConversationId: selectionConversationId });
      },

      chatStreamingMessages: null,
      setChatStreamingMessages: (messages) => {
        set({ chatStreamingMessages: messages });
      },

      setChatMessageContext: (messageId, context) => {
        const messages = get().chatStreamingMessages;

        if (!messages) {
          return;
        }

        const newMessages = messages?.map((message) => {
          if (message.id === messageId) {
            return { ...message, message_context: context };
          }

          return message;
        });

        set({ chatStreamingMessages: newMessages });
      },

      resetAiActionStore: () => {
        set({
          actionType: ACTION_TYPES.GENERATE,
          selectedConversationId: null,
          chatStreamingMessages: null,
          reasoningModel: AI_MODEL.CLAUDE_4_0_SONNET,
          codeGenerationModel: AI_MODEL.CLAUDE_4_0_SONNET,
          canvasPromptInput: "",
          chatPromptInput: "",
          stopAiTrigger: 0,
          tokenSoftLimit: 200,
          streamingTokenCount: 0,
        });
      },

      tokenSoftLimit: 200,
      setTokenSoftLimit: (limit) => {
        set({ tokenSoftLimit: limit });
      },

      stopAiTrigger: 0,
      setStopAiTrigger: () => {
        set({ stopAiTrigger: get().stopAiTrigger + 1 });
      },

      canvasPromptInput: "",
      chatPromptInput: "",
      setPromptInput: (type, prompt) => {
        if (type === "canvas") {
          set({ canvasPromptInput: prompt });
        } else {
          set({ chatPromptInput: prompt });
        }
      },

      streamingTokenCount: 0,
      setStreamingTokenCount: (count) => {
        set({ streamingTokenCount: count });
      },
      resetStreamingTokenCount: () => {
        set({ streamingTokenCount: 0 });
      },
    };
  }
);

export const useCurrentConvo = () => {
  const conversationId = useAiActionStore(
    (state) => state.selectedConversationId
  );
  const conversations = useSupabaseStore((state) => state.aiChatConversations);
  const messages = useSupabaseStore((state) => state.aiChatMessages);

  return useMemo(() => {
    // @ts-expect-error TS2538
    const conversation = conversations[conversationId];

    if (!conversation) {
      return {};
    }

    const messagesForConvo: Message[] = Object.values(messages)
      .filter((message) => message.conversation_id === conversationId)
      ?.sort((a, b) => {
        return (
          new Date(a?.created_at).getTime() - new Date(b?.created_at).getTime()
        );
      });

    return {
      conversation,
      messages: messagesForConvo,
    };
  }, [conversationId, conversations, messages]);
};
